import React, { useState, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../AppContext";
import { Link } from "react-router-dom";
import "../components/Auth.css";

export const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { port, setIsLoggedIn, setUserID, setToken, addAlert } = useContext(AppContext);

  const onSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    axios.post(`${port}/auth/login`, {
      username,
      password,
    })
      .then((res) => {
        setUserID(res.data.userID);
        setToken(res.data.token);
        setIsLoggedIn(true);
        navigate("/home");
      })
      .catch((err) => {
        addAlert("Invalid email or password", "danger");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleForgotPassword = async () => {
    if (!username) {
      setError("Please enter an email address");
      return;
    }

    setIsLoading(true);
    setError("");
    setResetMessage("");

    axios.post(`${port}/auth/forgot-password`, { username })
      .then(() => {
        setResetMessage(
          "Password reset link sent! Please check both your inbox and spam folder."
        );
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Email not found");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="auth-container">
      <h2 className="auth-title">Login</h2>
      <form className="auth-form" onSubmit={onSubmit}>
        <div className="form-group">
          <input
            type="email"
            placeholder="Email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className="form-group">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="auth-buttons">
          <button 
            type="submit" 
            className="auth-button" 
            disabled={isLoading}
          >
            {isLoading ? "Logging in..." : "Login"}
          </button>
          <button 
            type="button"
            className="text-button"
            onClick={handleForgotPassword}
            disabled={isLoading}
          >
            {isLoading ? "Sending..." : "Forgot Password?"}
          </button>
        </div>
        {error && <div className="auth-error">{error}</div>}
        {resetMessage && <div className="auth-message">{resetMessage}</div>}
      </form>
      <div className="auth-switch">
        Not a Member? <Link to="/register">Sign up</Link>
      </div>
    </div>
  );
};

export const Register = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { port, setIsLoggedIn, setUserID, setToken, addAlert } = useContext(AppContext);
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    matching: false
  });

  const validateEmail = (email) => {
    return email.match(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
  };

  const validateForm = () => {
    // Reset error
    setError("");

    // Email validation
    if (!validateEmail(username)) {
      setError("Please enter a valid email address");
      return false;
    }

    // Password length check
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return false;
    }

    // Password match check
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    // Password complexity check
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!(hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar)) {
      setError("Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character");
      return false;
    }

    return true;
  };

  const checkRequirements = (password, confirmPass) => {
    setRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      matching: password === confirmPass && password !== ''
    });
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    checkRequirements(newPassword, confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    checkRequirements(password, newConfirmPassword);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${port}/auth/register`, {
        username,
        password,
      });

      // If registration successful, attempt to log in
      const loginRes = await axios.post(`${port}/auth/login`, {
        username,
        password,
      });
      
      setUserID(loginRes.data.userID);
      setToken(loginRes.data.token);
      setIsLoggedIn(true);
      navigate("/home");
    } catch (err) {
      addAlert("There was an error registering. You may already be registered. Please try logging in.", "danger", 5000);
      setError("Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <h2 className="auth-title">Register</h2>
      <form className="auth-form" onSubmit={onSubmit}>
        <div className="form-group">
          <input
            type="email"
            placeholder="Email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isLoading}
            required
          />
        </div>
        <div className="form-group">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            disabled={isLoading}
            required
          />
          <div className="password-requirements">
            <div className={`requirement ${requirements.length ? 'met' : ''}`}>
              <i className={`fas fa-${requirements.length ? 'check' : 'times'}`}></i>
              At least 8 characters
            </div>
            <div className={`requirement ${requirements.uppercase ? 'met' : ''}`}>
              <i className={`fas fa-${requirements.uppercase ? 'check' : 'times'}`}></i>
              One uppercase letter
            </div>
            <div className={`requirement ${requirements.lowercase ? 'met' : ''}`}>
              <i className={`fas fa-${requirements.lowercase ? 'check' : 'times'}`}></i>
              One lowercase letter
            </div>
            <div className={`requirement ${requirements.number ? 'met' : ''}`}>
              <i className={`fas fa-${requirements.number ? 'check' : 'times'}`}></i>
              One number
            </div>
            <div className={`requirement ${requirements.special ? 'met' : ''}`}>
              <i className={`fas fa-${requirements.special ? 'check' : 'times'}`}></i>
              One special character
            </div>
          </div>
        </div>
        <div className="form-group">
          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            disabled={isLoading}
            required
          />
          <div className={`requirement ${requirements.matching ? 'met' : ''}`}>
            <i className={`fas fa-${requirements.matching ? 'check' : 'times'}`}></i>
            Passwords match
          </div>
        </div>
        {error && <div className="auth-error">{error}</div>}
        <button 
          type="submit" 
          className="auth-button"
          disabled={isLoading}
        >
          {isLoading ? "Registering..." : "Register"}
        </button>
      </form>
      <div className="auth-switch">
        Already a Member? <Link to="/login">Login</Link>
      </div>
    </div>
  );
};